import React, { Component } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Table } from "reactstrap";
import Moment from "moment";
import { apiFetch, API } from "../services/api";

function getParticipants(challengeId) {
    return apiFetch(API.back + '/challenge-participants?id=' + encodeURIComponent(challengeId), { method: 'GET' })
        .then(res => res.json());
}

function setExcluded(challengeId, userID, excluded) {
    return apiFetch(API.back + '/challenge-participant-exclude', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: challengeId, userID, excluded }),
    });
}

// Export CSV (séparateur « ; », BOM UTF-8 pour Excel) : type = leaderboard | rewards.
function downloadExport(challengeId, type) {
    return apiFetch(API.back + '/challenge-export?id=' + encodeURIComponent(challengeId) + '&type=' + type, { method: 'GET' })
        .then(res => res.text())
        .then(csv => {
            const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `challenge-${challengeId}-${type}-${Moment().format("YYYY-MM-DD")}.csv`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        });
}

// Paliers atteints : le back renvoie les champs sans tag JSON (Points, RewardID, ReachedAt, Notified).
const tierPoints = (t) => t.points !== undefined ? t.points : t.Points;
const tierNotified = (t) => t.notified !== undefined ? t.notified : t.Notified;
const tierAwarded = (t) => t.awarded !== undefined ? t.awarded : t.Awarded;

// Participants d'un challenge (classement complet, exclusion, exports).
export default class ChallengeParticipants extends Component {
    state = { participants: null, error: null, busy: null, exporting: null };

    componentDidMount() {
        this.refresh();
    }

    refresh = () => getParticipants(this.props.challenge.id)
        .then(data => this.setState({ participants: Array.isArray(data) ? data : [], error: null }))
        .catch(() => this.setState({ error: "Erreur lors de la récupération des participants" }));

    toggleExcluded = (p) => {
        this.setState({ busy: p.userID });
        setExcluded(this.props.challenge.id, p.userID, !p.optOutLeaderboard)
            .then(() => this.refresh())
            .catch(() => this.setState({ error: "Erreur lors de la mise à jour" }))
            .finally(() => this.setState({ busy: null }));
    };

    export = (type) => {
        this.setState({ exporting: type });
        downloadExport(this.props.challenge.id, type)
            .catch(() => this.setState({ error: "Erreur lors de l'export" }))
            .finally(() => this.setState({ exporting: null }));
    };

    render() {
        const { participants, error, busy, exporting } = this.state;
        // Lots attribués par palier (posés par le scorer) face au stock du challenge.
        const tierDefs = (this.props.challenge.tiers || []).slice().sort((a, b) => a.points - b.points);
        // Version courante du règlement (même empreinte que côté API : sha256 des textes par langue, 12 hex).
        const regulationVersion = this.props.challenge.regulationVersion || "";
        const stockLines = participants ? tierDefs.map(td => {
            const awarded = participants.reduce((n, p) => n + (p.tiersReached || []).filter(t => tierPoints(t) === td.points && tierAwarded(t)).length, 0);
            return { points: td.points, quantity: td.quantity || 0, awarded, soldOut: (td.quantity || 0) > 0 && awarded >= td.quantity };
        }) : [];
        const c = this.props.challenge;
        const suspicious = (participants || []).filter(p => p.suspicious).length;
        return (
            <Modal show={this.props.isOpen} onHide={this.props.closeModal} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Participants — {c.name}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                        <Button variant="secondary" size="sm" onClick={() => this.export("leaderboard")} disabled={!!exporting}>
                            {exporting === "leaderboard" ? "Export…" : "Exporter le classement (CSV)"}
                        </Button>
                        <Button variant="secondary" size="sm" onClick={() => this.export("rewards")} disabled={!!exporting}>
                            {exporting === "rewards" ? "Export…" : "Exporter les lots à envoyer (CSV)"}
                        </Button>
                        <Button variant="light" size="sm" onClick={this.refresh}>Rafraîchir</Button>
                        {participants ? <span className="text-muted">{participants.length} participant{participants.length === 1 ? "" : "s"}{suspicious ? ` · ${suspicious} à vérifier` : ""}</span> : null}
                    </div>
                    {participants && stockLines.length ? (
                        <p className="text-muted" style={{ fontSize: 13 }}>
                            Lots : {stockLines.map(l => `${l.points} pts → ${l.awarded}${l.quantity ? ` / ${l.quantity}` : ""} attribué${l.awarded > 1 ? "s" : ""}${l.soldOut ? " (épuisé)" : ""}`).join(" · ")}
                        </p>
                    ) : null}
                    {error ? <p style={{ color: "#c0392b" }}>{error}</p> : null}
                    {!participants ? <p>Chargement ...</p> :
                        <Table responsive size="sm">
                            <thead>
                            <tr>
                                <th>Rang</th>
                                <th>Nom</th>
                                <th>Email</th>
                                <th>Points</th>
                                <th>Visites</th>
                                <th>Comptes</th>
                                <th>Partages</th>
                                <th>Paliers</th>
                                <th>Inscrit le</th>
                                <th>Règlement</th>
                                <th></th>
                            </tr>
                            </thead>
                            <tbody>
                            {participants.map(p => {
                                const b = p.breakdown || {};
                                const tiers = p.tiersReached || [];
                                return (
                                    <tr key={p.userID} style={p.suspicious ? { background: "#fff4e5" } : p.optOutLeaderboard ? { color: "#999" } : undefined}
                                        title={p.suspicious ? `À vérifier : ${p.suspiciousReason}` : undefined}>
                                        <td>{p.rank > 0 ? p.rank : "—"}</td>
                                        <td>{p.displayName}{p.suspicious ? " ⚠️" : ""}{p.optOutLeaderboard ? " (exclu)" : ""}</td>
                                        <td>{p.email}</td>
                                        <td><strong>{p.points}</strong></td>
                                        <td>{b.visits || 0}</td>
                                        <td>{b.signups || 0}</td>
                                        <td>{(b.shares || 0) + (b.bonus || 0)}</td>
                                        <td title="palier (× = stock épuisé, lot non attribué ; * = pas encore notifié)">
                                            {tiers.length ? tiers.map(t => `${tierPoints(t)}${tierAwarded(t) ? "" : "×"}${tierNotified(t) ? "" : "*"}`).join(", ") : "—"}
                                        </td>
                                        <td>{p.joinedAt ? Moment(p.joinedAt).format("DD/MM HH:mm") : "—"}</td>
                                        <td title={p.regulationAcceptedAt ? `accepté le ${Moment(p.regulationAcceptedAt).format("DD/MM/YYYY HH:mm")} (version ${p.regulationVersion})` : "jamais accepté"}>
                                            {!regulationVersion ? "—" : p.regulationVersion === regulationVersion ? "✓" : p.regulationAcceptedAt ? "à ré-accepter" : "non"}
                                        </td>
                                        <td>
                                            <Button variant={p.optOutLeaderboard ? "outline-success" : "outline-danger"} size="sm"
                                                    disabled={busy === p.userID} onClick={() => this.toggleExcluded(p)}>
                                                {p.optOutLeaderboard ? "Réintégrer" : "Exclure"}
                                            </Button>
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </Table>}
                    <p className="text-muted" style={{ fontSize: 12 }}>
                        Ligne orange = journée hors norme (visites ou comptes) à vérifier avant l'envoi des lots. « * » sur un palier = email de palier non parti.
                        Un exclu garde ses points mais sort du classement public et ne décale personne.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={this.props.closeModal}>Fermer</Button>
                </Modal.Footer>
            </Modal>
        );
    }
}
